package com.zi.support.shared

/** One finished piece of work: a ticket closed, a call handled, a subtask or Kanban task done or a site visit signed, by [uid] at [atMillis]. */
data class LeaderboardWork(val uid: String, val name: String, val kind: String, val atMillis: Double, val priority: String = "")

data class LeaderboardEntry(
    val uid: String, val name: String, val points: Int, val tickets: Int, val calls: Int, val subtasks: Int,
    val tasks: Int = 0, val visits: Int = 0,
    /** High, urgent or critical tickets closed (also counted in [tickets]). */
    val urgent: Int = 0,
)

/** Everyone's score for one period (best first) and the team's totals, with last period's points for the trend. */
data class LeaderboardBoard(
    val entries: List<LeaderboardEntry>,
    val teamPoints: Int, val teamTickets: Int, val teamCalls: Int, val teamSubtasks: Int,
    val previousTeamPoints: Int, val teamTasks: Int = 0, val teamVisits: Int = 0,
)

/**
 * More > Leaderboard on web, Android and iOS. Each app reads the finished work since [previousStart] (tickets by
 * completedAt, calls by startedAt, subtasks by completedAt, Kanban tasks by [taskDoneAt], site visits by createdAt)
 * and scores it here, so all three show the same numbers.
 * ponytail: scored on the device from about two periods of records; move to a server tally if that grows past a few thousand.
 */
object Leaderboard {
    const val TICKET = "ticket"
    const val CALL = "call"
    const val SUBTASK = "subtask"
    /** A Kanban task moved to Done, credited to its assignee (or whoever finished it if unassigned). */
    const val TASK = "task"
    /** A signed site visit record, credited to whoever captured it. */
    const val VISIT = "visit"
    /** Category only: high, urgent or critical tickets closed. */
    const val URGENT = "urgent"
    val PERIODS: List<String> = listOf("week", "month")
    val CATEGORIES: List<String> = listOf("points", TICKET, URGENT, TASK, SUBTASK, CALL, VISIT)
    /** What the page explains under its title. */
    const val SCORING = "Points: tickets closed (high 3, normal 2, low 1), site visits 2, and calls, subtasks and Kanban tasks 1 each."

    private const val DAY = 86_400_000.0
    /** South Africa is UTC+2 all year, the same days the call stats use. */
    private const val SAST = 2 * 3_600_000.0

    fun periodLabel(period: String): String = if (period == "month") "This month" else "This week"
    fun previousLabel(period: String): String = if (period == "month") "last month" else "last week"

    fun categoryLabel(category: String): String = when (category) {
        TICKET -> "Tickets"
        CALL -> "Calls"
        SUBTASK -> "Subtasks"
        TASK -> "Kanban tasks"
        VISIT -> "Site visits"
        URGENT -> "High priority"
        else -> "Overall"
    }

    /** The unit after a score: "pts", "tickets", "site visits"… */
    fun unit(category: String): String = when (category) {
        TICKET -> "tickets"; CALL -> "calls"; SUBTASK -> "subtasks"; TASK -> "tasks"; VISIT -> "visits"; URGENT -> "high priority"; else -> "pts"
    }

    /** The line under each person: what they did, leaving out what they didn't ("4 tickets · 2 tasks · 1 visit"). */
    fun summary(entry: LeaderboardEntry): String = listOf(
        entry.tickets to "ticket", entry.tasks to "task", entry.subtasks to "subtask", entry.calls to "call", entry.visits to "visit",
    ).filter { it.first > 0 }.joinToString(" · ") { (n, word) -> "$n $word${if (n == 1) "" else "s"}" }.ifBlank { "Nothing yet" }

    private fun isUrgent(work: LeaderboardWork) = work.kind == TICKET && work.priority.lowercase() in setOf("high", "urgent", "critical")

    /**
     * When a Kanban task was finished: its completedAt (stamped by the apps when it moves to Done), else for older tasks
     * when its timer last stopped. 0 means unknown, and it isn't counted.
     */
    fun taskDoneAt(completedAtMillis: Double, timerStoppedMillis: Double): Double = if (completedAtMillis > 0) completedAtMillis else timerStoppedMillis

    /** Who a finished task counts for: its assignee, or whoever moved it to Done when nobody was assigned. */
    fun taskOwner(assignedUid: String, assignedName: String, completedByUid: String, completedByName: String): Pair<String, String> =
        if (assignedUid.isNotBlank()) assignedUid to assignedName else completedByUid to completedByName

    /** A closed ticket is worth its priority (high 3, normal 2, low 1); a handled call or a subtask is worth 1. */
    fun points(work: LeaderboardWork): Int = when (work.kind) {
        TICKET -> when (work.priority.lowercase()) { "high", "urgent", "critical" -> 3; "low" -> 1; else -> 2 }
        VISIT -> 2
        else -> 1
    }

    /** Only tickets that are still finished count, so one reopened after a rushed close stops scoring. */
    fun countsTicket(status: String): Boolean = status.lowercase() in TicketPolicy.FINISHED_STATUSES

    /** Answered calls that are work (not personal or spam) count for whoever took them. */
    fun countsCall(outcome: String, personal: Boolean, spam: Boolean): Boolean = CallLogPolicy.isAnswered(outcome) && !personal && !spam

    /** Monday 00:00 (week) or the 1st 00:00 (month) in SAST, as epoch millis. */
    fun periodStart(period: String, nowMillis: Double): Double {
        val day = kotlin.math.floor((nowMillis + SAST) / DAY).toLong()
        val first = if (period == "month") day - (civil(day).third - 1) else day - ((day + 3) % 7)  // 1970-01-01 was a Thursday
        return first * DAY - SAST
    }

    /** The start of the period before this one. */
    fun previousStart(period: String, nowMillis: Double): Double = periodStart(period, periodStart(period, nowMillis) - 1)

    /** Scores [work] done from [start]: everyone best first, ties by name; work before [start] and after [previousStart] is last period. */
    fun build(work: List<LeaderboardWork>, start: Double, previousStart: Double): LeaderboardBoard {
        val current = work.filter { it.uid.isNotBlank() && it.atMillis >= start }
        val entries = current.groupBy { it.uid }.map { (uid, done) ->
            LeaderboardEntry(uid, done.map { it.name }.firstOrNull { it.isNotBlank() } ?: "Support user", done.sumOf(::points),
                done.count { it.kind == TICKET }, done.count { it.kind == CALL }, done.count { it.kind == SUBTASK },
                done.count { it.kind == TASK }, done.count { it.kind == VISIT }, done.count(::isUrgent))
        }.sortedWith(compareByDescending<LeaderboardEntry> { it.points }.thenBy { it.name.lowercase() })
        return LeaderboardBoard(entries, entries.sumOf { it.points }, entries.sumOf { it.tickets }, entries.sumOf { it.calls }, entries.sumOf { it.subtasks },
            work.filter { it.uid.isNotBlank() && it.atMillis >= previousStart && it.atMillis < start }.sumOf(::points),
            entries.sumOf { it.tasks }, entries.sumOf { it.visits })
    }

    /** The value [category] ranks by. */
    fun score(entry: LeaderboardEntry, category: String): Int = when (category) {
        TICKET -> entry.tickets
        CALL -> entry.calls
        SUBTASK -> entry.subtasks
        TASK -> entry.tasks
        VISIT -> entry.visits
        URGENT -> entry.urgent
        else -> entry.points
    }

    /** Everyone with something in [category], best first. */
    fun ranked(board: LeaderboardBoard, category: String): List<LeaderboardEntry> =
        board.entries.filter { score(it, category) > 0 }.sortedWith(compareByDescending<LeaderboardEntry> { score(it, category) }.thenBy { it.name.lowercase() })

    /** "Up 8 on last week", "Down 3 on last month", "Same as last week". */
    fun trend(board: LeaderboardBoard, period: String): String {
        val change = board.teamPoints - board.previousTeamPoints
        return when {
            change > 0 -> "Up $change on ${previousLabel(period)}"
            change < 0 -> "Down ${-change} on ${previousLabel(period)}"
            else -> "Same as ${previousLabel(period)}"
        }
    }

    fun initials(name: String): String = name.split(' ').filter { it.isNotBlank() }.take(2).joinToString("") { it.take(1) }.uppercase().ifBlank { "?" }

    /** (year, month, day) for days since 1970-01-01 (Howard Hinnant's civil_from_days). */
    internal fun civil(days: Long): Triple<Int, Int, Int> {
        val z = days + 719468
        val era = (if (z >= 0) z else z - 146096) / 146097
        val doe = z - era * 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = (doy - (153 * mp + 2) / 5 + 1).toInt()
        val m = (if (mp < 10) mp + 3 else mp - 9).toInt()
        return Triple((yoe + era * 400 + if (m <= 2) 1 else 0).toInt(), m, d)
    }

    /**
     * The 3D podium for the top three of [ranked] in [category]: gold, silver and bronze blocks rise in, each winner's
     * initials spin on a coin above their block, a crown floats over first place and confetti falls. Shown in an iframe on
     * the web, a WebView on Android and a WKWebView on iOS (like the ticket Route page). Without WebGL it draws a flat podium.
     */
    fun podiumPage(ranked: List<LeaderboardEntry>, category: String): String {
        val top = ranked.take(3).joinToString(",") { e ->
            "{name:${TicketLocation.jsString(e.name)},initials:${TicketLocation.jsString(initials(e.name))},score:${score(e, category)}}"
        }
        return PODIUM_PAGE.replace("__TOP__", "[$top]").replace("__UNIT__", TicketLocation.jsString(unit(category)))
    }

    // No dollar signs below: Kotlin string. three.js r128 from cdnjs, the version the web app has used before.
    private const val PODIUM_PAGE = """<!doctype html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
<title>Podium</title>
<style>
:root{color-scheme:light dark;--text:#17202a;--muted:#748091}
@media (prefers-color-scheme:dark){:root{--text:#f1f4f7;--muted:#8f9baa}}
html,body{margin:0;height:100%;overflow:hidden;background:transparent;font:600 13px system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:var(--text)}
canvas{display:block;width:100vw!important;height:100vh!important}
.flat{display:flex;align-items:flex-end;justify-content:center;gap:10px;height:100%;padding:0 16px;box-sizing:border-box}
.flat div{flex:1;max-width:120px;border-radius:12px 12px 0 0;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding-top:10px;color:#fff;animation:rise .9s cubic-bezier(.2,.8,.2,1) both}
.flat b{font-size:22px}.flat small{opacity:.85}
@keyframes rise{from{transform:translateY(100%);opacity:0}}
.empty{display:grid;place-items:center;height:100%;color:var(--muted)}
</style></head><body>
<script src="https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js"></script>
<script>
var top3 = __TOP__, unit = __UNIT__;
var still = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;
var dark = window.matchMedia && matchMedia("(prefers-color-scheme: dark)").matches;
// Podium order on screen: 2nd, 1st, 3rd.
var slots = [{rank: 1, x: -2.25, h: 1.5, color: 0xc0c7d1, label: "2"}, {rank: 0, x: 0, h: 2.3, color: 0xf5b82e, label: "1"}, {rank: 2, x: 2.25, h: 1.05, color: 0xcd7f43, label: "3"}];
function webgl() { try { var c = document.createElement("canvas"); return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl"))); } catch (e) { return false; } }
function flat() {
  if (!top3.length) { document.body.innerHTML = '<div class="empty">No points yet. The first finished ticket takes the top spot.</div>'; return; }
  var colors = {1: "#9aa3ae", 0: "#e0a21a", 2: "#b8692f"}, heights = {1: 62, 0: 86, 2: 46};
  document.body.innerHTML = '<div class="flat">' + slots.map(function (s, i) {
    var p = top3[s.rank]; return '<div style="height:' + heights[s.rank] + '%;background:' + colors[s.rank] + ';animation-delay:' + (i * 0.12) + 's"><b>' + s.label + "</b>" +
      (p ? "<span>" + p.name.replace(/</g, "&lt;") + "</span><small>" + p.score + " " + unit + "</small>" : "") + "</div>";
  }).join("") + "</div>";
}
if (!window.THREE || !webgl()) flat(); else scene3d();
// Text drawn on a canvas, used as a texture (coin faces, block numbers, floating labels).
function textTexture(lines, w, h, bg, fg, pill) {
  var c = document.createElement("canvas"); c.width = w; c.height = h; var g = c.getContext("2d");
  if (bg && pill) { var r = h / 2.4; g.fillStyle = bg; g.beginPath(); g.moveTo(r, 0); g.arcTo(w, 0, w, h, r); g.arcTo(w, h, 0, h, r); g.arcTo(0, h, 0, 0, r); g.arcTo(0, 0, w, 0, r); g.closePath(); g.fill(); }
  else if (bg) { g.fillStyle = bg; g.fillRect(0, 0, w, h); }
  g.fillStyle = fg; g.textAlign = "center"; g.textBaseline = "middle";
  lines.forEach(function (l) { g.font = l.font; g.fillText(l.text, w / 2, l.y); });
  var t = new THREE.CanvasTexture(c); t.anisotropy = 4; return t;
}
function scene3d() {
  var renderer = new THREE.WebGLRenderer({antialias: true, alpha: true});
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputEncoding = THREE.sRGBEncoding;
  document.body.appendChild(renderer.domElement);
  var scene = new THREE.Scene();
  var camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  scene.add(new THREE.HemisphereLight(0xffffff, dark ? 0x1b2633 : 0xdfe7f1, 0.85));
  var key = new THREE.DirectionalLight(0xffffff, 0.9); key.position.set(3, 8, 6); scene.add(key);
  var rim = new THREE.PointLight(0x7aa7ff, 0.8, 30); rim.position.set(-5, 4, -3); scene.add(rim);
  var floor = new THREE.Mesh(new THREE.CircleGeometry(4.4, 64), new THREE.MeshBasicMaterial({color: dark ? 0x3b82f6 : 0x2563eb, transparent: true, opacity: dark ? 0.16 : 0.08}));
  floor.rotation.x = -Math.PI / 2; scene.add(floor);
  var blocks = [], coins = [], labels = [], crown = null;
  slots.forEach(function (s, i) {
    var person = top3[s.rank];
    var mat = new THREE.MeshStandardMaterial({color: s.color, metalness: 0.35, roughness: 0.35});
    var face = new THREE.MeshStandardMaterial({map: textTexture([{text: s.label, font: "800 150px system-ui", y: 128}], 256, 256, "#" + s.color.toString(16).padStart(6, "0"), "rgba(255,255,255,.92)"), metalness: 0.4, roughness: 0.35});
    // Box faces: +x, -x, +y, -y, +z (front, shows the place), -z.
    var block = new THREE.Mesh(new THREE.BoxGeometry(2, s.h, 1.6), [mat, mat, mat, mat, face, mat]);
    block.userData = {h: s.h, delay: i * 0.18};
    block.position.set(s.x, -s.h / 2, 0); block.scale.y = 0.001; scene.add(block); blocks.push(block);
    if (!person) return;
    // The coin stands up facing the camera (cylinder turned on its side) and spins about the vertical axis in a group.
    var faceMap = textTexture([{text: person.initials, font: "800 110px system-ui", y: 128}], 256, 256, "#" + s.color.toString(16).padStart(6, "0"), "#ffffff");
    faceMap.center.set(0.5, 0.5); faceMap.rotation = Math.PI / 2;
    var coinFace = new THREE.MeshStandardMaterial({map: faceMap, metalness: 0.55, roughness: 0.3});
    var edge = new THREE.MeshStandardMaterial({color: s.color, metalness: 0.45, roughness: 0.3});
    var disc = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.16, 48), [edge, coinFace, coinFace]);
    disc.rotation.x = Math.PI / 2;
    var coin = new THREE.Group(); coin.add(disc); coin.userData = {base: s.h + 0.95, delay: i * 0.18 + 0.5, phase: i};
    coin.position.set(s.x, s.h + 0.95, 0); coin.scale.setScalar(0.001); scene.add(coin); coins.push(coin);
    var label = new THREE.Sprite(new THREE.SpriteMaterial({map: textTexture([
      {text: person.name.length > 16 ? person.name.slice(0, 15) + "…" : person.name, font: "700 56px system-ui", y: 52},
      {text: person.score + " " + unit, font: "600 44px system-ui", y: 116}], 512, 160, "rgba(15,23,42,.94)", "#ffffff", true), transparent: true, depthWrite: false}));
    label.scale.set(2.6, 0.81, 1); label.position.set(s.x, s.h + (s.rank === 0 ? 2.75 : 2.05), 0); label.material.opacity = 0; label.userData = {delay: i * 0.18 + 0.8};
    scene.add(label); labels.push(label);
    if (s.rank === 0) {
      // A small crown: a gold band with five points, floating over first place.
      crown = new THREE.Group();
      var gold = new THREE.MeshStandardMaterial({color: 0xffc83d, metalness: 0.3, roughness: 0.3, emissive: 0x6b4a00, emissiveIntensity: 0.35, side: THREE.DoubleSide});
      crown.add(new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.56, 0.3, 32, 1, true), gold));
      for (var k = 0; k < 5; k++) { var a = k / 5 * Math.PI * 2; var spike = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.42, 12), gold); spike.position.set(Math.cos(a) * 0.5, 0.36, Math.sin(a) * 0.5); crown.add(spike);
        var gem = new THREE.Mesh(new THREE.SphereGeometry(0.07, 12, 12), new THREE.MeshStandardMaterial({color: [0xdc2626, 0x2563eb, 0x0d9488][k % 3], roughness: 0.2})); gem.position.set(Math.cos(a) * 0.53, 0.02, Math.sin(a) * 0.53); crown.add(gem); }
      crown.position.set(s.x, s.h + 1.8, 0); crown.scale.setScalar(0.001); crown.userData = {delay: 1.3, base: s.h + 1.8}; scene.add(crown);
    }
  });
  if (!top3.length) { var note = document.createElement("div"); note.className = "empty"; note.style.cssText = "position:absolute;inset:0;align-items:start;padding-top:18px"; note.textContent = "No points yet. The first finished ticket takes the top spot."; document.body.appendChild(note); }
  // Confetti: little paper squares that flutter down and start again at the top.
  var confetti = [];
  if (!still && top3.length) {
    var palette = [0xf5b82e, 0x2563eb, 0x0d9488, 0xdc2626, 0x8b5cf6, 0xf97316];
    var paper = new THREE.PlaneGeometry(0.09, 0.14);
    for (var n = 0; n < 140; n++) {
      var piece = new THREE.Mesh(paper, new THREE.MeshBasicMaterial({color: palette[n % palette.length], side: THREE.DoubleSide}));
      piece.position.set((Math.random() - 0.5) * 9, 4 + Math.random() * 8, (Math.random() - 0.5) * 4);
      piece.userData = {speed: 0.6 + Math.random() * 0.9, spin: (Math.random() - 0.5) * 6, sway: Math.random() * 6};
      scene.add(piece); confetti.push(piece);
    }
  }
  var pointer = {x: 0, y: 0};
  window.addEventListener("pointermove", function (e) { pointer.x = e.clientX / innerWidth - 0.5; pointer.y = e.clientY / innerHeight - 0.5; });
  function resize() {
    var w = innerWidth, h = innerHeight; renderer.setSize(w, h, false); camera.aspect = w / h;
    // Pull back on narrow screens so all three blocks fit.
    camera.position.z = w / h < 1 ? 13 / (w / h) * 0.62 : 11.5; camera.updateProjectionMatrix();
  }
  // The frame can get its real size after this runs (iframes, web views), so follow it rather than the first measurement.
  addEventListener("resize", resize); resize();
  if (window.ResizeObserver) new ResizeObserver(resize).observe(document.documentElement);
  function ease(t) { t = Math.min(Math.max(t, 0), 1); return 1 - Math.pow(1 - t, 3) * (1 - 2.2 * t * (1 - t)); }
  var start = performance.now(), last = start;
  function frame(now) {
    var t = (now - start) / 1000, dt = Math.min((now - last) / 1000, 0.05); last = now;
    blocks.forEach(function (b) { var k = still ? 1 : ease((t - b.userData.delay) / 0.9); b.scale.y = Math.max(k, 0.001); b.position.y = b.userData.h * k / 2; });
    coins.forEach(function (c) {
      var k = still ? 1 : ease((t - c.userData.delay) / 0.7); c.scale.setScalar(Math.max(k, 0.001));
      c.position.y = c.userData.base + (still ? 0 : Math.sin(t * 1.6 + c.userData.phase) * 0.08);
      // Mostly facing forward: a slow full turn every few seconds, so the initials stay readable.
      c.rotation.y = still ? 0 : Math.sin(t * 0.9 + c.userData.phase) * 0.6 + (Math.sin(t * 0.3 + c.userData.phase) > 0.97 ? (t * 6) % (Math.PI * 2) : 0);
    });
    labels.forEach(function (l) { l.material.opacity = still ? 1 : Math.min(Math.max((t - l.userData.delay) / 0.5, 0), 1); });
    if (crown) { var kc = still ? 1 : ease((t - crown.userData.delay) / 0.6); crown.scale.setScalar(Math.max(kc, 0.001)); crown.rotation.y = still ? 0 : t * 0.8; crown.position.y = crown.userData.base + (still ? 0 : Math.sin(t * 2) * 0.06); }
    confetti.forEach(function (p) {
      var u = p.userData; p.position.y -= u.speed * dt; p.position.x += Math.sin(t * 1.5 + u.sway) * dt * 0.4;
      p.rotation.x += u.spin * dt; p.rotation.y += u.spin * 0.7 * dt; if (p.position.y < 0) p.position.y = 7 + Math.random() * 3;
    });
    camera.position.x += (pointer.x * 2.2 - camera.position.x) * 0.05;
    camera.position.y += (3.4 - pointer.y * 1.4 - camera.position.y) * 0.05;
    camera.lookAt(0, 2.5, 0);
    renderer.render(scene, camera);
    requestAnimationFrame(frame);
  }
  camera.position.y = 3.4; requestAnimationFrame(frame);
}
</script></body></html>"""
}
